import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { User, Settings, LogOut, Globe } from 'lucide-react';
import { languageNames, supportedLanguages } from '../i18n/config';

const Layout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">S</span>
                </div>
                <span className="text-xl font-bold text-gray-900">ScanVCF</span>
              </Link>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex space-x-8">
              <Link 
                to="/dashboard" 
                className={`text-sm font-medium ${
                  location.pathname === '/dashboard' 
                    ? 'text-primary-600' 
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {t('nav.tab.home')}
              </Link>
              <Link 
                to="/card-editor" 
                className={`text-sm font-medium ${
                  location.pathname === '/card-editor' 
                    ? 'text-primary-600' 
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {t('nav.tab.addCard')}
              </Link>
              <Link 
                to="/profile" 
                className={`text-sm font-medium ${
                  location.pathname === '/profile' 
                    ? 'text-primary-600' 
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {t('navigation.settings')}
              </Link>
            </nav>

            {/* Right side */}
            <div className="flex items-center space-x-4">
              {/* Language Selector */}
              <div className="relative">
                <select
                  value={i18n.language}
                  onChange={(e) => changeLanguage(e.target.value)}
                  className="appearance-none bg-white border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 min-w-[140px]"
                >
                  {supportedLanguages.map(lang => (
                    <option key={lang} value={lang}>
                      {languageNames[lang] || lang}
                    </option>
                  ))}
                </select>
              </div>

              {/* User Menu */}
              <div className="relative">
                <button className="flex items-center space-x-2 text-gray-500 hover:text-gray-900">
                  <User size={20} />
                  <span className="text-sm font-medium">User</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-gray-500 text-sm">
            <p>&copy; 2024 ScanVCF. All rights reserved.</p>
            <p className="mt-2">
              <a href="https://scanvcf.com" className="text-primary-600 hover:text-primary-700">
                scanvcf.com
              </a>
              {' • '}
              <a href="https://app.scanvcf.com" className="text-primary-600 hover:text-primary-700">
                app.scanvcf.com
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout; 